import React, { Fragment, useContext } from 'react'
import { useTranslation } from 'react-i18next'
import { isEmpty, isUndefined } from 'lodash'
import cn from 'classnames'
import PropTypes from 'prop-types'
import { Edit20 } from '@carbon/icons-react'
import { Row } from '@ublique/components/Grid'
import Button from '@ublique/components/Button'
import { FORM_HIDDEN, HIDDEN } from '../constants'
import useStoreSelector from 'utils/hooks/useStoreSelector'
import useDateTimeFormat from 'utils/hooks/useDateTimeFormat'
import { getDataString } from 'components/Wizard/helpers/recap'
import { ScenarioWizardComponentsContext } from 'components/Wizard'

const InputRowHeader = ({ t, value, onEdit, idx }) => {
  const getStepLabel = (key) => {
    const platformLabel = t(`WizardPlatform:${key}`)
    return platformLabel === key ? t(key) : platformLabel
  }
  return (
    <Fragment>
      <hr className={`step-recap-line-${idx}`} />
      <div className={`recap-input-row-header row-${idx}`}>
        <h5>{getStepLabel(value)}</h5>
        <Edit20 onClick={onEdit} />
      </div>
    </Fragment>
  )
}

const InputRowBody = ({
  t,
  data,
  status,
  wrapRecap,
  inputElements,
  isScheduledMode,
  payloadMapper
}) => {
  const externalComponents = useContext(ScenarioWizardComponentsContext)
  const formatOptions = useDateTimeFormat()

  return (
    <div
      className={cn('recap-input-row-values-container', {
        'recap-col-6': wrapRecap
      })}
    >
      {Object.keys(data).map((elementId) => {
        const inputProps = inputElements[elementId]
        const value = data[elementId]
        const isHidden = status[elementId]?.some(
          ({ state }) => state === HIDDEN || state === FORM_HIDDEN
        )

        if (isUndefined(value) || value === '' || isHidden) {
          return null
        }

        return (
          <div
            key={elementId}
            className={cn('recap-input-row-values', {
              'recap-col-6': wrapRecap
            })}
          >
            <h6>{t(inputElements[elementId].label)}</h6>
            <span>
              {getDataString({
                ...inputProps,
                recapElement: externalComponents?.[inputProps.recapElement],
                formatOptions,
                elementId,
                value,
                t,
                formattedDate: true,
                isScheduledMode,
                payloadMapper
              })}
            </span>
          </div>
        )
      })}
    </div>
  )
}

const RecapFooter = ({ t, onCancelClick, onSecondaryClick, onSubmit }) => (
  <Row className='buttons-footer'>
    <a onClick={onCancelClick}>{t('WizardPlatform:SecondaryButtonLabel')}</a>
    <Button kind='secondary' onClick={onSecondaryClick}>
      {t('WizardPlatform:Back')}
    </Button>
    <Button onClick={onSubmit}>{t('WizardPlatform:SaveButtonLabel')}</Button>
  </Row>
)

const Recap = ({ steps, setStep, changeOpen, wizardSave }) => {
  const { t } = useTranslation(['Wizard', 'WizardPlatform'])
  const { inputElements, values, status, isScheduledMode, payloadMapper } =
    useStoreSelector('wizard')

  if (isEmpty(values)) {
    return null
  }

  return (
    <div id='recap'>
      <div className='overflow-box'>
        {Object.keys(values).map((stepIndex, idx) => {
          const { title, wrapRecap = false } = steps[stepIndex]

          return (
            <Row key={stepIndex} className='recap-input-row'>
              <InputRowHeader
                t={t}
                idx={idx}
                value={t(title)}
                onEdit={() => setStep(parseInt(stepIndex))}
              />

              <InputRowBody
                t={t}
                status={status}
                wrapRecap={wrapRecap}
                data={values[stepIndex]}
                inputElements={inputElements}
                isScheduledMode={isScheduledMode}
                payloadMapper={payloadMapper}
              />
            </Row>
          )
        })}
      </div>

      <RecapFooter
        t={t}
        onSubmit={wizardSave}
        onCancelClick={() => changeOpen(false)}
        onSecondaryClick={() => setStep((step) => step - 1)}
      />
    </div>
  )
}

Recap.propTypes = {
  steps: PropTypes.array.isRequired,
  setSteps: PropTypes.func,
  changeOpen: PropTypes.func,
  wizardSave: PropTypes.func
}

Recap.defaultProps = {
  setSteps: () => {},
  changeOpen: () => {},
  wizardSave: () => {}
}

export default React.memo(Recap)
